import { useState, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, ArrowRightLeft, Check } from 'lucide-react'
import { apiGet, apiPost } from '@/lib/api'
import { cn } from '@/lib/utils'

interface Property {
  id: string
  name: string
  code: string
}

interface Building {
  id: string
  name: string
  code: string
  floors: Floor[]
}

interface Floor {
  id: string
  name: string
  number: number
  rooms: Room[]
}

interface Room {
  id: string
  name: string
  number: string
}

interface PropertyDetail {
  id: string
  name: string
  buildings: Building[]
}

interface Props {
  assetId: string
  assetName: string
  currentPropertyId: string
  currentPropertyName: string
  currentLocation: string
  onClose: () => void
  onSuccess: () => void
}

const MOVEMENT_TYPES = [
  { value: 'DISPATCH', label: 'Dispatch', desc: 'Send to another property', icon: '→' },
  { value: 'ARRIVAL', label: 'Arrival', desc: 'Arrived at this location', icon: '←' },
  { value: 'CHECKOUT', label: 'Check Out', desc: 'Assign to a room or person', icon: '↗' },
  { value: 'CHECKIN', label: 'Check In', desc: 'Return from room or person', icon: '↙' },
  { value: 'MAINTENANCE_OUT', label: 'Maintenance Out', desc: 'Send for repair / refurb', icon: '⚙' },
  { value: 'MAINTENANCE_IN', label: 'Maintenance In', desc: 'Returned from repair', icon: '✓' },
  { value: 'AUDIT', label: 'Audit Scan', desc: 'Confirm current location', icon: '✔' },
]

export function AssetTransferModal({
  assetId,
  assetName,
  currentPropertyId,
  currentPropertyName,
  currentLocation,
  onClose,
  onSuccess,
}: Props) {
  const queryClient = useQueryClient()
  const [movementType, setMovementType] = useState('')
  const [toPropertyId, setToPropertyId] = useState('')
  const [toBuildingId, setToBuildingId] = useState('')
  const [toFloorId, setToFloorId] = useState('')
  const [toRoomId, setToRoomId] = useState('')
  const [checkedOutTo, setCheckedOutTo] = useState('')
  const [notes, setNotes] = useState('')
  const [success, setSuccess] = useState(false)

  const { data: properties } = useQuery({
    queryKey: ['properties'],
    queryFn: () => apiGet<Property[]>('/properties'),
  })

  // Load destination property detail for building/floor/room selection
  const destinationPid = movementType === 'DISPATCH' ? toPropertyId : currentPropertyId
  const { data: destProperty } = useQuery({
    queryKey: ['property-detail', destinationPid],
    queryFn: () => apiGet<PropertyDetail>(`/properties/${destinationPid}`),
    enabled: !!destinationPid && ['DISPATCH', 'ARRIVAL', 'CHECKOUT'].includes(movementType),
  })

  const buildings = destProperty?.buildings ?? []
  const selectedBuilding = buildings.find((b) => b.id === toBuildingId)
  const floors = selectedBuilding?.floors ?? []
  const selectedFloor = floors.find((f) => f.id === toFloorId)
  const rooms = selectedFloor?.rooms ?? []

  const needsDestProperty = movementType === 'DISPATCH'
  const needsLocation = ['ARRIVAL', 'CHECKOUT'].includes(movementType)
  const needsCheckedOutTo = movementType === 'CHECKOUT'

  const transferMutation = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { movementType }
      if (toPropertyId) body.toPropertyId = toPropertyId
      if (toBuildingId) body.toBuildingId = toBuildingId
      if (toFloorId) body.toFloorId = toFloorId
      if (toRoomId) body.toRoomId = toRoomId
      if (checkedOutTo) body.checkedOutTo = checkedOutTo
      if (notes) body.notes = notes
      return apiPost(`/assets/nfc/assets/${assetId}/transfer`, body)
    },
    onSuccess: () => {
      setSuccess(true)
      queryClient.invalidateQueries({ queryKey: ['asset', assetId] })
      queryClient.invalidateQueries({ queryKey: ['asset-movements', assetId] })
      queryClient.invalidateQueries({ queryKey: ['assets'] })
      setTimeout(() => {
        onSuccess()
        onClose()
      }, 1500)
    },
  })

  const canSubmit = movementType && !transferMutation.isPending && (
    !needsDestProperty || toPropertyId
  )

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-lg font-bold text-navy-800">Transfer Recorded</h2>
          <p className="text-sm text-gray-500 mt-1">{assetName}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <ArrowRightLeft className="w-5 h-5 text-navy-800" />
            <div>
              <h2 className="text-lg font-bold text-navy-800">Transfer Asset</h2>
              <p className="text-xs text-gray-500">{assetName} — {currentLocation}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Movement type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">What's happening?</label>
            <div className="grid grid-cols-2 gap-2">
              {MOVEMENT_TYPES.map((mt) => (
                <button
                  key={mt.value}
                  type="button"
                  onClick={() => {
                    setMovementType(mt.value)
                    setToPropertyId('')
                    setToBuildingId('')
                    setToFloorId('')
                    setToRoomId('')
                    setCheckedOutTo('')
                  }}
                  className={cn(
                    'text-left p-3 rounded-xl border-2 transition-colors',
                    movementType === mt.value
                      ? 'border-navy-800 bg-navy-50'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{mt.icon}</span>
                    <span className="text-sm font-medium text-navy-800">{mt.label}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{mt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Destination property (for DISPATCH) */}
          {needsDestProperty && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Destination Property</label>
              <select
                value={toPropertyId}
                onChange={(e) => {
                  setToPropertyId(e.target.value)
                  setToBuildingId('')
                  setToFloorId('')
                  setToRoomId('')
                }}
                className="input"
              >
                <option value="">Select property...</option>
                {properties?.filter((p) => p.id !== currentPropertyId).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Location (building/floor/room) for ARRIVAL, CHECKOUT */}
          {needsLocation && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
              <div className="space-y-2">
                <select
                  value={toBuildingId}
                  onChange={(e) => {
                    setToBuildingId(e.target.value)
                    setToFloorId('')
                    setToRoomId('')
                  }}
                  className="input"
                >
                  <option value="">Building...</option>
                  {buildings.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                {toBuildingId && (
                  <select
                    value={toFloorId}
                    onChange={(e) => {
                      setToFloorId(e.target.value)
                      setToRoomId('')
                    }}
                    className="input"
                  >
                    <option value="">Floor...</option>
                    {floors.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                )}
                {toFloorId && (
                  <select
                    value={toRoomId}
                    onChange={(e) => setToRoomId(e.target.value)}
                    className="input"
                  >
                    <option value="">Room...</option>
                    {rooms.map((r) => (
                      <option key={r.id} value={r.id}>{r.number} - {r.name}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          )}

          {/* Checked out to (for CHECKOUT) */}
          {needsCheckedOutTo && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Checked Out To</label>
              <input
                type="text"
                value={checkedOutTo}
                onChange={(e) => setCheckedOutTo(e.target.value)}
                placeholder="e.g. Room 204, John Smith, Housekeeping"
                className="input"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              rows={2}
              className="input"
            />
          </div>

          {/* Error */}
          {transferMutation.isError && (
            <p className="text-sm text-red-600">
              {(transferMutation.error as any)?.response?.data?.error ?? 'Transfer failed'}
            </p>
          )}

          {/* Submit */}
          <button
            type="button"
            onClick={() => transferMutation.mutate()}
            disabled={!canSubmit}
            className="w-full py-3 bg-navy-800 text-white rounded-xl font-semibold hover:bg-navy-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {transferMutation.isPending ? 'Recording...' : 'Record Transfer'}
          </button>
        </div>
      </div>
    </div>
  )
}
